<template lang="pug">
	
	div.shape-input
		div.input-group(@click="selectFile()", :title="fileName")
		
			div.input-group-prepend.input-group-btn
				button.btn.btn-sm.btn-primary(type="button") 
					i.mdi.mdi-folder-open
					|  Selecionar

			input#inputFile(ref="inputFile", type="file", 
					accept=".zip, .shp, .SHP", 
					style="display: none;", 
					@change="onChange")

			input.form-control.form-control-sm(type="text", readonly="true", :value="fileName")

		small.text-muted(v-show="!file") Shapes de até {{ maxFileSize }} MB nos formatos shp ou zip.

		small.text-success(v-show="file && !loading && !error") Shape carregado com sucesso.

		div.progress(v-show="loading")
			div.progress-bar.progress-bar-striped.progress-bar-animated(
					role="progressbar", 
					aria-valuenow="75", 
					aria-valuemin="0", 
					aria-valuemax="100")
				| Processando arquivo
</template>

<script>

import shp from 'shpjs';
import GeoJsonUtil from '@/utils/geoJsonUtil';

export default {
	
	name: 'ShapeInput',

	props: {

		value: Object,

		maxFileSize: {
			type: Number,
			default: 100
		},

		acceptPolygons: {
			type: Boolean,
			default: false
		},

		acceptPoints: {
			type: Boolean,
			default: false
		},

		acceptLineStrings: {
			type: Boolean,
			default: false
		},

		maxGeometries: {
			type: Number,
			default: -1
		}
	},

	inject: ['$validator'],

	data () {
		return {
			fileName: null,
			file: null,
			reader: null,
			error: null,
			loading: false
		};
	},

	mounted () {

		var that = this;

		this.$validator.addComponentRule(this, {
			getMessage: field => (that.error && that.error.msg) || 'Não foi possível processar o arquivo shape selecionado.',
			validate: (value) => !that.error
		});
	},

	methods: {

		clear () {
			this.fileName = null;
			this.file = null;
			this.reader = null;
			this.error = null;
			this.loading = false;
			this.$refs.inputFile.value = '';
		},

		selectFile () {

			this.$refs.inputFile.click();
		},

		onChange (evt) {
			
			if (!this.$refs.inputFile.files || this.$refs.inputFile.files.length === 0) return;

			this.file = this.$refs.inputFile.files[0];
			this.fileName = this.file.name;
			this.error = null;

			if (this.file.size > this.maxFileSizeInBytes) {

				this.handleError({ msg: 'O shape selecionado excede o tamanho máximo de ' + this.maxFileSize + ' MB.' });
				return;
			}

			this.loading = true;

			setTimeout(() => {
				this.handleFile(this.file);
			}, 1);
		},

		handleFile (file) {

			this.reader = new FileReader();
			var that = this;
			var extension = file.name.slice(-3);

			this.reader.onload = function () {

				if (this.readyState !== 2 || this.error) {

					this.handleError(this.error);

				} else {

					if (extension === 'zip') {
						that.parseZipData(this.result);
					} else {
						that.parseShpData(this.result);
					}
				}
			};
			
			this.$emit('startLoading');
			this.reader.readAsArrayBuffer(file);
		},

		parseZipData (data) {

			shp(data).then((geoJson) => {

				this.loading = false;
				this.submitGeoJson(geoJson);

			}, (e) => {

				this.handleError(e);
			});
		},

		parseShpData (data, cb, scope) {

			try {
				var geometries = shp.parseShp(data);
				this.loading = false;

				var geoJson = {
					type: 'FeatureCollection',
					features: [],
					fileName: this.fileName
				};

				for (var geometry of geometries) {

					geoJson.features.push({
						type: 'Feature',
						geometry: geometry
					});
				}

				this.submitGeoJson(geoJson);

			} catch (e) {

				this.handleError(e);
			}
		},

		submitGeoJson (geoJson) {

			let details = GeoJsonUtil.getDetails(geoJson);
			let error = null;

			if (details.totalGeometries === 0) {

				error = 'O shape selecionado não possui geometrias.';
			
			} else if (!this.validateGeometryType(details)) {

				error = this.typesErrorMsg;

			} else if (this.maxGeometries > 0 && details.totalGeometries > this.maxGeometries) {

				error = 'O shape deve conter no máximo ' + this.maxGeometries +
					((this.maxGeometries > 1) ? ' geometrias.' : ' geometria.');
			}

			if (error) {

				this.handleError({ msg: error });

			} else {

				this.$emit('input', geoJson);
			}
			
		},

		validateGeometryType (details) {

			if (this.acceptAllTypes) return true;

			return ((!details.isPolygon || this.acceptPolygons) &&
					(!details.isPoint || this.acceptPoints) &&
					(!details.isLineString || this.acceptLineStrings) &&
					(details.isPolygon || details.isPoint || details.isLineString));
		},

		handleError (error) {

			this.loading = false;
			this.error = error;
			this.$emit('input', error);
		}
	},

	computed: {

		maxFileSizeInBytes () {

			return this.maxFileSize * 1024 * 1024;
		},

		acceptAllTypes () {

			return !this.acceptPolygons && !this.acceptPoints && !this.acceptLineStrings;
		},

		typesErrorMsg () {

			if (this.acceptAllTypes) return '';

			let types = [];

			if (this.acceptPolygons) types.push('polígonos');
			if (this.acceptLineStrings) types.push('linhas');
			if (this.acceptPoints) types.push('pontos');

			let msg = 'O shape selecionado deve conter apenas ';
			
			if (types.length === 1) {

				msg += types[0];

			} else {

				for (let i = 0; i < types.length - 1; i++) {

					if (i > 0) msg += ', ';
					msg += types[i];
				}

				msg += ' ou ' + types[types.length - 1];
			}

			return msg + '.';
		}
	}
};

</script>

<style lang="scss" scoped>

@import "../../styles/variaveis";
@import "../../styles/mixins";

.progress {
	margin-top: 5px;
	font-weight: bold;
	height: 13px;
	font-size: 11px;
	color: #FFF;
	@include border-radius(3px);

	.progress-bar {
		width: 100%;
	}
}

input.form-control {
	background-color: #FFF;
}

.shape-input.invalid {

	.text-muted, .text-success {
		display: none;
	}
}

</style>


// WEBPACK FOOTER //
// ShapeInput.vue?82de5466